import { addBreadcrumb } from './observability';

/**
 * Call a function and gracefully return `null` and do nothing if it throws.
 * Can easily add a fallback with nullish coalescing.
 */
// ? possibly export or share elsewhere
function tryIt<R>(fn: () => R) {
  try {
    return fn();
  } catch {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'Storage',
      message: 'Storage access failed',
    });
    return null;
  }
}

type StorageType = Extract<keyof Window, 'localStorage' | 'sessionStorage'>;

/**
 * A wrapper around any `Storage` object that will gracefully do nothing or fallback if the storage is disabled.
 * [Document: hasStorageAccess](https://developer.mozilla.org/en-US/docs/Web/API/Document/hasStorageAccess) might provide another way to check if storage is available, but it is not widely supported yet.
 */
export function gracefulStorage(storage: StorageType) {
  return Object.freeze({
    get length() {
      return tryIt(() => window[storage].length) ?? 0;
    },

    clear() {
      tryIt(() => window[storage].clear());
    },

    getItem(key: string) {
      return tryIt(() => window[storage].getItem(key));
    },

    key(index: number) {
      return tryIt(() => window[storage].key(index));
    },

    removeItem(key: string) {
      tryIt(() => window[storage].removeItem(key));
    },

    setItem(key: string, value: string) {
      tryIt(() => window[storage].setItem(key, value));
    },
  } satisfies Storage);
}
